import React, { useState } from 'react';
import { Table, Button, Space, Popconfirm, message, Image, Typography, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { deleteProductVariation } from '../../../core/api/products';

const { Text } = Typography;

const ExistingVariationsTable = ({ variations, loading, onEdit, onRefresh, productId }) => {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (variationId) => {
    try {
      setDeletingId(variationId);
      await deleteProductVariation(variationId);
      message.success('Variation deleted successfully');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      message.error('Failed to delete variation');
      console.error('Error deleting variation:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusColor = (statusName) => {
    const statusColors = {
      'Active': 'green',
      'Inactive': 'red',
      'Pending': 'orange',
      'Draft': 'blue',
    };
    
    return statusColors[statusName] || 'default';
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status_name',
      key: 'status_name',
      render: (text) => (
        <Tag color={getStatusColor(text)}>{text || 'Unknown'}</Tag>
      ),
    },
    {
      title: 'Variation Type',
      dataIndex: 'product_variation_type_name',
      key: 'product_variation_type_name',
      render: (text) => text || '—',
    },
    {
      title: 'Variation',
      dataIndex: 'product_variation',
      key: 'product_variation',
      render: (text) => text || '—',
    },
    {
      title: 'Program Time',
      dataIndex: 'program_time',
      key: 'program_time',
      render: (text) => text || '—',
    },
    {
      title: 'Posology',
      dataIndex: 'posology',
      key: 'posology',
      render: (text) => text || '—',
    },
    {
      title: 'Logistic Code',
      dataIndex: 'logistic_codification',
      key: 'logistic_codification',
      render: (text) => text || '—',
    },
    {
      title: 'Variation Code',
      dataIndex: 'product_variation_code',
      key: 'product_variation_code',
      render: (text) => text || '—',
    },
    {
      title: 'Description',
      dataIndex: 'product_variation_desc',
      key: 'product_variation_desc',
      ellipsis: true,
      render: (text) => (
        <Text ellipsis={{ tooltip: text }}>
          {text || '—'}
        </Text>
      ),
    },
    {
      title: 'Photos',
      dataIndex: 'images',
      key: 'images',
      render: (images) => {
        if (!images || images.length === 0) {
          return <Text type="secondary">No images</Text>;
        }
        
        return (
          <Image.PreviewGroup>
            <div style={{ display: 'flex', gap: '4px' }}>
              {images.slice(0, 3).map((img, index) => (
                <Image
                  key={index}
                  src={img.signed_image_url || img.image}
                  alt={`variation-${index}`}
                  width={30}
                  height={30}
                  style={{ objectFit: 'cover', borderRadius: '4px' }}
                />
              ))}
              {images.length > 3 && (
                <div 
                  style={{ 
                    width: 30, 
                    height: 30, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  +{images.length - 3}
                </div>
              )}
            </div>
          </Image.PreviewGroup>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this variation?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.id}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={variations}
      rowKey="id"
      loading={loading}
      pagination={{ pageSize: 10 }}
      scroll={{ x: 'max-content' }}
      size="middle"
    />
  );
};

export default ExistingVariationsTable;
